import {
  Building2,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Menu,
  MessageSquare,
  MoonIcon,
  SunIcon,
  X,
} from "lucide-react";
import {
  feedbackContextNote,
  insertFeedbackContextNote,
  useRecordFeedback,
} from "@lytra/frontend-common";
import UserDropdown from "@/components/UserDropdown";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useCurrentTheme, useTheme } from "@/providers/ThemeProvider";
import { useTranslation } from "react-i18next";
import { Nav } from "@/components/navigation/Nav";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { FileUploadProvider } from "@/providers/FileUploadProvider";
import * as Sentry from "@sentry/react";
import { useFeature, useFeatureEnabled, useProfile } from "@/providers/ProfileProvider";
import { getMenuItems } from "@/lib/navigation";
import { useAgentSync } from "@/components/agent/useAgentSync";
import { cn } from "@/lib/utils";
import { useAssistantChatFeature } from "@/hooks/useAssistantChatFeature";
import { VersionUpdateBanner } from "@/components/VersionUpdateBanner";
import { useTenantSelection } from "@/providers/TenantSelectionProvider";
import { usePageTitle } from "@/hooks/usePageTitle";
import { LayoutInsetContext, type LayoutInsetControls } from "@/layouts/layoutInset";
import { ArchiveSearchDialogProvider } from "@/components/customer/archive/ArchiveSearchDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";

export default function SidebarLayout() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const currentTheme = useCurrentTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(
    () => searchParams.get("nav") !== "expanded",
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [fullBleed, setFullBleed] = useState(false);
  const layoutInset = useMemo<LayoutInsetControls>(() => ({ setFullBleed }), []);
  const [topbarOpen, setTopbarOpen] = useState(false);
  const feedbackButtonRef = useRef<HTMLButtonElement>(null);
  const recordFeedback = useRecordFeedback();

  // Close mobile nav on route change. This synchronizes state with an
  // external system (the router), which is exactly what effects are for —
  // the lint rule's "avoid setState in effects" heuristic doesn't apply here.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileNavOpen(false);
  }, [location.pathname]);

  const { userRoles } = useProfile();

  // Drives the live-connection dot on the "Service Assistant" nav item.
  // Skipped for non-agents to avoid an idle subscription + poll on every page
  // for users who never see that nav entry.
  const agentSync = useAgentSync({ skip: !userRoles.isAgent });

  // Lytra staff rely on the tenant selector, so open the utility pill by default
  // for them once their role is known. Runs only when isLytraStaff changes, so a
  // staff member who manually closes the pill keeps it closed. Same
  // external-system-sync case as above.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (userRoles.isLytraStaff) setTopbarOpen(true);
  }, [userRoles.isLytraStaff]);

  const {
    selectedTenantId,
    setSelectedTenantId,
    tenants,
    loading: tenantsLoading,
  } = useTenantSelection();

  // Compute feature flags
  const featureDashboard = useFeature("dashboard");
  // useFeature prepends "feature.", and the default matches the
  // declaration: assistant chat is on unless a tenant turns it off.
  const featureAssistantChat = useFeatureEnabled("assistant-chat", true);
  const featureLiveChat = useFeature("live-chat");
  const featureCalls = useFeature("calls");
  const featureDocuments = useFeature("documents");
  const featureDbTables = useFeature("db-tables");
  const featureServiceAssistant = useFeatureEnabled("service-assistant", true);
  const featureOntologyLookup = useFeatureEnabled("ontology-lookup", true);
  const featureUsageStatistics = useFeatureEnabled("usage-statistics", true);
  const featureServiceKpis = useFeatureEnabled("service-kpis", false);
  const featureTicketing = useFeature("ticketing");
  const featureCustomerChat = useFeature("customer-chat");

  //business logic
  const { assistantChatEnabled } = useAssistantChatFeature();

  // Update page title based on current route
  usePageTitle();

  useEffect(() => {
    const feedback = Sentry.getFeedback();
    let cleanup: (() => void) | undefined;

    if (feedback && feedbackButtonRef.current) {
      cleanup = feedback.attachTo(feedbackButtonRef.current, {
        formTitle: t("nav.feedbackTitle"),
        colorScheme: currentTheme,
        showBranding: false,
        triggerLabel: t("nav.feedbackTrigger"),
        triggerAriaLabel: t("nav.feedbackTrigger"),
        submitButtonLabel: t("nav.feedbackSubmitButton"),
        cancelButtonLabel: t("nav.feedbackCancelButton"),
        confirmButtonLabel: t("nav.feedbackConfirmButton"),
        addScreenshotButtonLabel: t("nav.feedbackAddScreenshot"),
        removeScreenshotButtonLabel: t("nav.feedbackRemoveScreenshot"),
        nameLabel: t("nav.feedbackName"),
        namePlaceholder: t("nav.feedbackNamePlaceholder"),
        emailLabel: t("nav.feedbackEmail"),
        emailPlaceholder: t("nav.feedbackEmailPlaceholder"),
        isRequiredLabel: t("nav.feedbackRequired"),
        messageLabel: t("nav.feedbackMessage"),
        messagePlaceholder: t("nav.feedbackMessagePlaceholder"),
        successMessageText: t("nav.feedbackSuccess"),
        onSubmitSuccess: recordFeedback,
        onFormOpen: () => {
          // Convert the rolling background buffer (armed by
          // replaysOnErrorSampleRate: 1 in main.tsx) into an uploaded replay and
          // attach it to this feedback report. Without this, most sessions have
          // no replay running and a feedback report only ever shows the few
          // seconds of the user filling out the form itself.
          void Sentry.getReplay()?.flush();

          // The widget renders in a shadow DOM. Its form column uses
          // `justify-content: space-between`, which — inside a flex container
          // that grows toward the viewport height — pushes the action buttons
          // to the very bottom and leaves a large empty gap. Inject a style
          // override so the dialog sizes to its content instead.
          const shadowRoot = document.querySelector(
            "#sentry-feedback",
          )?.shadowRoot;
          if (shadowRoot && !shadowRoot.querySelector("#lytra-feedback-fix")) {
            const style = document.createElement("style");
            style.id = "lytra-feedback-fix";
            style.textContent = `
              .form__right { justify-content: flex-start; }
              .dialog__content { align-self: flex-start; }
            `;
            shadowRoot.appendChild(style);
          }

          if (shadowRoot) {
            insertFeedbackContextNote(shadowRoot, feedbackContextNote(i18n.language));
          }
        },
      });
    }

    return () => {
      if (cleanup) {
        try {
          cleanup();
        } catch (error) {
          // Silently catch cleanup errors from Sentry feedback widget
          console.warn("Failed to cleanup Sentry feedback widget:", error);
        }
      }
    };
  }, [currentTheme, t, i18n.language, recordFeedback]);

  // Get menu items from shared utility
  const { topLinks, bottomLinks } = useMemo(
    () =>
      getMenuItems({
        userRoles: {
          isAdmin: userRoles.isAdmin,
          isLytraStaff: userRoles.isLytraStaff,
          isAgent: userRoles.isAgent,
        },
        features: {
          dashboard: featureDashboard,
          assistantChat: featureAssistantChat,
          liveChat: featureLiveChat,
          calls: featureCalls,
          documents: featureDocuments,
          assistantChatEnabled,
          dbTables: featureDbTables,
          serviceAssistant: featureServiceAssistant,
          ontologyLookup: featureOntologyLookup,
          usageStatistics: featureUsageStatistics,
          serviceKpis: featureServiceKpis,
          ticketing: featureTicketing,
          customerChat: featureCustomerChat,
        },
      }),
    [
      userRoles.isAdmin,
      userRoles.isLytraStaff,
      userRoles.isAgent,
      featureDashboard,
      featureAssistantChat,
      featureLiveChat,
      featureCalls,
      featureDocuments,
      assistantChatEnabled,
      featureDbTables,
      featureServiceAssistant,
      featureOntologyLookup,
      featureServiceKpis,
      featureTicketing,
      featureCustomerChat,
      featureUsageStatistics,
    ],
  );

  const tenantSelector = userRoles.isLytraStaff && (
    <div className="flex w-full items-center gap-2 px-3 pb-2">
      <Building2 className="h-4 w-4 text-muted-foreground" />
      <Select
        value={selectedTenantId}
        onValueChange={setSelectedTenantId}
        disabled={tenantsLoading}
      >
        <SelectTrigger className="h-9 w-full">
          <SelectValue placeholder={t("nav.selectTenant")} />
        </SelectTrigger>
        <SelectContent>
          {tenants.map((tenant) => (
            <SelectItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <LayoutInsetContext value={layoutInset}>
      <TooltipProvider delayDuration={0}>
        <FileUploadProvider>
          <ArchiveSearchDialogProvider>
          <div className="grid w-full h-screen min-h-screen grid-cols-1 md:grid-cols-[auto_1fr]">
            {/* Desktop sidebar */}
            <div className="hidden md:flex flex-col h-full max-h-screen min-h-0 gap-2 bg-muted/40 border-r group">
              <div className="flex h-14 items-center px-4 lg:h-[60px] shrink-0">
                <Link
                  to="/"
                  className={cn(
                    "flex items-center gap-2 font-semibold",
                    isCollapsed && "group-hover:hidden",
                  )}
                >
                  {isCollapsed ? (
                    <>
                      <img src="/lytra_icon_dark.svg" className="size-6 dark:hidden" />
                      <img src="/lytra_icon_white.svg" className="size-6 hidden dark:block" />
                    </>
                  ) : (
                    <>
                      <img src="/lytra_logo_dark.svg" className="size-20 ml-1 dark:hidden" />
                      <img src="/lytra_logo_white.svg" className="size-20 ml-1 hidden dark:block" />
                    </>
                  )}
                </Link>
                <button
                  type="button"
                  onClick={() => setIsCollapsed((v) => !v)}
                  aria-label={
                    isCollapsed ? t("nav.expand") : t("nav.collapse")
                  }
                  className={cn(
                    "hidden group-hover:flex size-6 items-center justify-center rounded-md border border-border hover:border-muted-foreground/60 transition-colors cursor-pointer",
                    !isCollapsed && "ml-auto",
                  )}
                >
                  {isCollapsed ? (
                    <ChevronsRight className="h-4 w-4" />
                  ) : (
                    <ChevronsLeft className="h-4 w-4" />
                  )}
                </button>
              </div>
              <Nav
                className="flex-1"
                isCollapsed={isCollapsed}
                topLinks={topLinks}
                bottomLinks={bottomLinks}
                agentOnline={agentSync.neptuneOnline}
                showSettings={userRoles.isAdmin}
              />
            </div>

            {/* Mobile sidebar (sheet) */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetContent side="left" className="w-[280px] p-0 md:hidden">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="flex h-14 items-center px-4 shrink-0">
                    <Link
                      to="/"
                      className="flex items-center gap-2 font-semibold"
                    >
                      <img src="/lytra_logo_dark.svg" className="size-20 ml-1 dark:hidden" />
                      <img src="/lytra_logo_white.svg" className="size-20 ml-1 hidden dark:block" />
                    </Link>
                  </div>
                  {tenantSelector}
                  <Nav
                    className="flex-1"
                    isCollapsed={false}
                    topLinks={topLinks}
                    bottomLinks={bottomLinks}
                    agentOnline={agentSync.neptuneOnline}
                    showSettings={userRoles.isAdmin}
                  />
                </div>
              </SheetContent>
            </Sheet>

            <div className="relative flex flex-col max-h-screen h-screen overflow-hidden">
              {/* Mobile hamburger - floats top-left over content */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden absolute top-2 left-2 z-20 shrink-0 rounded-full border bg-background/80 shadow-sm backdrop-blur"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">{t("nav.openNavigation")}</span>
              </Button>

              {/* Utility controls - collapsible floating pill, top-right.
                  The toggle stays fixed at the right edge; the controls unroll
                  leftward into their full state on click. */}
              <div className="absolute top-2 right-2 z-20 flex max-w-[calc(100vw-4rem)] items-center gap-1 rounded-full border bg-background/80 px-1.5 py-1 shadow-sm backdrop-blur">
                <div
                  className={`flex items-center gap-1 overflow-hidden transition-all duration-300 ease-in-out ${
                    topbarOpen
                      ? "max-w-[640px] opacity-100"
                      : "max-w-0 opacity-0 pointer-events-none"
                  }`}
                >
                  <Button
                    ref={feedbackButtonRef}
                    variant="ghost"
                    size="sm"
                    className="font-medium rounded-full cursor-pointer"
                  >
                    <MessageSquare className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t("nav.feedback")}</span>
                  </Button>
                  {/* Tenant selector, compact on mobile so staff reach it without
                      opening the sheet (which also has one). */}
                  {userRoles.isLytraStaff && (
                    <div className="flex min-w-0 items-center gap-2 border-l pl-2">
                      <Building2 className="hidden h-4 w-4 text-muted-foreground sm:block" />
                      <Select
                        value={selectedTenantId}
                        onValueChange={setSelectedTenantId}
                        disabled={tenantsLoading}
                      >
                        <SelectTrigger className="h-9 w-[120px] cursor-pointer sm:w-[160px] md:w-[200px]">
                          <SelectValue placeholder={t("nav.selectTenant")} />
                        </SelectTrigger>
                        <SelectContent>
                          {tenants.map((tenant) => (
                            <SelectItem
                              key={tenant.id}
                              value={tenant.id}
                              className="cursor-pointer"
                            >
                              {tenant.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full cursor-pointer"
                      >
                        <SunIcon className="h-5 w-5 rotate-0 scale-100 dark:-rotate-90 dark:scale-0" />
                        <MoonIcon className="absolute h-5 w-5 rotate-90 scale-0 dark:rotate-0 dark:scale-100" />
                        <span className="sr-only">{t("nav.toggleDarkMode")}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => theme.setTheme("light")}
                      >
                        {t("nav.themeLight")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => theme.setTheme("dark")}
                      >
                        {t("nav.themeDark")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => theme.setTheme("system")}
                      >
                        {t("nav.themeSystem")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <UserDropdown />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full cursor-pointer shrink-0"
                  onClick={() => setTopbarOpen((open) => !open)}
                  aria-expanded={topbarOpen}
                >
                  {topbarOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <ChevronLeft className="h-5 w-5" />
                  )}
                  <span className="sr-only">{t("nav.openNavigation")}</span>
                </Button>
              </div>

              <main
                className={cn(
                  "flex-1 flex flex-col overflow-auto",
                  fullBleed ? "p-0" : "pt-2 px-2 pb-0 md:pt-4 md:px-4 md:pb-0",
                )}
              >
                <VersionUpdateBanner />
                <div
                  className={cn(
                    "h-full w-full flex flex-col overflow-auto bg-background",
                    !fullBleed && "rounded-tl-lg",
                  )}
                >
                  <Outlet />
                  <Toaster />
                  <SonnerToaster />
                </div>
              </main>
            </div>
          </div>
          </ArchiveSearchDialogProvider>
        </FileUploadProvider>
      </TooltipProvider>
    </LayoutInsetContext>
  );
}
